import ChatDto from '@tera/shared/src/dto/ChatDto';
import React, { useCallback, useEffect, useReducer, useMemo } from 'react';
import { useRef } from 'react';
import { io } from 'socket.io-client';
import { ActionType, chatDispatchAction, chatReducer } from './chatReducer';
import { ChatMessagePayload } from '@tera/shared/src/types/ChatMessagePayload';
import { toast } from 'react-toastify';
import { nanoid } from 'nanoid';

// Função logger condicional que só imprime em desenvolvimento
const logger = (message: string, ...args: any[]) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log(`[WS] ${message}`, ...args);
  }
};

// Função de debounce para evitar excesso de eventos sem usar hooks
// Esta implementação não usa hooks, então pode ser usada em qualquer lugar
const createDebounce = <F extends (...args: any[]) => any>(
  func: F,
  waitFor: number
): ((...args: Parameters<F>) => void) => {
  let timeout: NodeJS.Timeout | undefined;

  return (...args: Parameters<F>): void => {
    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(() => func(...args), waitFor);
  };
};

// Som de nova mensagem: msga com a tela de chat ativa, msg caso contrário.
// Rajadas de mensagens tocam uma vez só.
let tocandoSomMensagem = false;
const tocarSomMensagem = (paginaAtiva: boolean) => {
  if (tocandoSomMensagem) return;
  tocandoSomMensagem = true;

  new Audio(paginaAtiva ? '/sounds/msga.mp3' : '/sounds/msg.mp3')
    .play()
    .catch(() => console.warn('Erro ao tocar som de nova mensagem'))
    .finally(() => setTimeout(() => { tocandoSomMensagem = false; }, 1000));
};

type useWebsocketProps = {
  userId?: number;
  /** Página visível (monitor de frames do page.tsx); escolhe o som de nova mensagem */
  isPageActive: React.RefObject<boolean>;
  userRole?: string;
  query: {
    search?: string;
    canalId?: number;
    chatStatus?: string;
    userId?: number;
    dataInicio?: string;
    dataFim?: string;
    chatTags?: string[];
    mode?: 'related' | 'recent';
    apenasMeus?: boolean;
    usuarioResponsavelId?: string[];
    grupoResponsavelId?: string[];
  };
  onLoadingChats: (loading: boolean) => void;
  onChatsUpdated: (chats: ChatDto[]) => void;
  onConnected: () => void;
  onAcceptedCall?: (id: number, userId: number) => void;
  onNewMessageWhileFiltered?: () => void;
  /** Indica se existem canais VoIP cadastrados */
  hasVoipCanais?: boolean;
};

type FilaCallEntry = {
  id: number;
  canal_cliente_id: string;
  fila_data_criacao: Date;
}

export type ChatState = {
  isConnected: boolean;
  chats: ChatDto[];
  selectedChatId: string | null;
  selectedChatClientId: string | null;
  selectedChatMessages: any;
  /**
   * Cópia da conversa aberta quando ela não está mais na lista carregada.
   * O botão "atualizar" recarrega só a primeira página, então uma conversa
   * aberta que estava numa página seguinte sairia de `chats` — e o painel,
   * que resolve a conversa procurando na lista, fecharia sozinho.
   */
  selectedChatSnapshot: ChatDto | null;
  chatsCursor: string | null;
  hasMoreChats: boolean;
  hasMorePages: boolean;
  loadingChats: boolean;
  fila: any[];
  filaCall: FilaCallEntry[];
  serverError: any;
  sendingMessage: boolean;
  params: Record<string, any> | null;
  /** Aviso sobre dispositivos de áudio (microfone/speakers) */
  audioDevicesWarning: string | null;
  /** Indica se o usuário pode atender chamadas (tem microfone + permissão) */
  canHandleCalls: boolean;
};

const chatInitialState: ChatState = {
  isConnected: false,
  chats: [],
  selectedChatId: null,
  selectedChatClientId: null,
  selectedChatMessages: null,
  selectedChatSnapshot: null,
  chatsCursor: null,
  hasMoreChats: false,
  hasMorePages: false,
  loadingChats: false,
  fila: [],
  filaCall: [],
  serverError: null,
  sendingMessage: false,
  params: null,
  audioDevicesWarning: null,
  canHandleCalls: true, // Assume true até verificar
};

const useWebsocket = ({
  userId,
  isPageActive,
  userRole,
  query,
  onLoadingChats,
  onChatsUpdated,
  onConnected,
  onAcceptedCall = () => { },
  onNewMessageWhileFiltered = () => { },
  hasVoipCanais = false,
}: useWebsocketProps) => {
  const [wsState, setWsState] = React.useState<
    'disconnected' | 'connecting' | 'connected' | 'error'
  >('connecting');
  const [chatState, chatDispatch] = useReducer(chatReducer, chatInitialState);

  // Usar useRef sem inicialização imediata do socket para evitar múltiplas instâncias
  const wsInstance = useRef<any>(null);

  // Inicializar o socket apenas uma vez
  if (!wsInstance.current) {
    wsInstance.current = io('/wsinternal', {
      timeout: 5000,
      autoConnect: false,
      withCredentials: true,
      transports: ['websocket'],
    });
  }

  const socket = wsInstance.current;

  const handleConnect = useCallback(() => {
    setWsState('connected');
    logger('connected');

    // Executar onConnected diretamente sem setTimeout desnecessário
    onConnected();
  }, [onConnected]);

  const handleDisconnect = useCallback(() => {
    setWsState('disconnected');
    logger('disconnected');

    chatDispatch({
      type: chatDispatchAction.SET_LOADING_CHATS as ActionType,
      payload: false,
    });
  }, []);

  const handleReconnectionAttempt = useCallback(() => {
    setWsState('connecting');
    logger('attempting to reconnect');
  }, []);

  const handleReconnect = useCallback(() => {
    setWsState('connected');
    logger('reconnected');

    // Garantir que o estado seja atualizado quando reconectar
    onConnected();
  }, [onConnected]);

  const handleConnectError = useCallback((err: any) => {
    setWsState('error');
    logger('connection error', err);
  }, []);

  const handleParams = useCallback((params: any) => {
    logger('received params', params);

    const { filas, filasCall, ...rest } = params;

    chatDispatch({
      type: chatDispatchAction.SET_PARAMS as ActionType,
      payload: rest,
    });

    if (params?.filas) {
      chatDispatch({
        type: chatDispatchAction.SET_FILA as ActionType,
        payload: filas,
      });
    }

    if (params?.filasCall) {
      chatDispatch({
        type: chatDispatchAction.SET_FILA_CALL as ActionType,
        payload: filasCall,
      });
    }
  }, [])

  // Marcada pelo `reloadChats` manual e lida na resposta (`chats`) que ele
  // provoca. Um reload disparado por filtro/busca no meio do caminho limpa a
  // marca — na dúvida, prevalece o comportamento antigo (fechar a conversa).
  const preservarSelecaoRef = useRef(false);

  const discardMessage = () => {
    onNewMessageWhileFiltered();
    return false;
  }

  // O filtro de atendente/grupo responsável vale só para atendimentos em
  // aberto: com o seletor marcado, chatbot, finalizado e afins saem da lista.
  // Quando marcado, ele substitui "Apenas meus" — as duas restrições juntas
  // (ex.: eu + outro atendente) zerariam a lista.
  const matchesIds = (ids: string[], value: any) =>
    ids.length === 0 || ids.includes(String(value));

  const matchesResponsavelFilter = (chat: any) => {
    const { usuarioResponsavelId = [], grupoResponsavelId = [] } = query;
    const comFiltroResponsavel = usuarioResponsavelId.length > 0 || grupoResponsavelId.length > 0;
    if (comFiltroResponsavel) {
      if (chat.estado !== 'atendimento') return false;
      return matchesIds(usuarioResponsavelId, chat.usuario_responsavel_id) &&
        matchesIds(grupoResponsavelId, chat.grupo_responsavel_id);
    }
    // Sem seleção: vale "Apenas meus" (comportamento antigo).
    return !query.apenasMeus || chat.usuario_responsavel_id === userId;
  }

  const handleChats = useCallback(
    (data: any) => {
      if (!data) return;

      // Filtragem dos chats recebidos conforme os filtros ativos
      const { items, cursor, prevCursor } = data;

      // Consome a intenção do reload que originou esta resposta: só o
      // "atualizar" manual mantém aberta uma conversa que saiu da lista.
      const preservarSelecao = preservarSelecaoRef.current;
      preservarSelecaoRef.current = false;
      const filteredItems = items?.filter((chat: any) => {
        if (query.canalId && chat.canal_id !== query.canalId) return discardMessage();
        if (query.chatStatus && chat.estado !== query.chatStatus) return discardMessage();
        if (!matchesResponsavelFilter(chat)) return discardMessage();
        // se a busca estiver definida, o conteúdo pode estar em mensagens anteriores. Melhor desabilitar neste caso aqui
        // if (query.search && !(`${chat.nome}`.toLowerCase().includes(query.search.toLowerCase()) || `${chat.canal_cliente_id}`.includes(query.search) || `${chat.ultmsg_conteudo_ou_url}`.includes(query.search))) return discardMessage();
        if (query.dataInicio && chat.ultmsg_data_criacao && new Date(chat.ultmsg_data_criacao) < new Date(query.dataInicio)) return discardMessage();
        if (query.dataFim && chat.ultmsg_data_criacao && new Date(chat.ultmsg_data_criacao) > new Date(query.dataFim)) return discardMessage();
        if (query.chatTags && query.chatTags.length > 0) {
          if (!chat.marcadores || chat.marcadores.length === 0) return discardMessage();
          const hasTag = query.chatTags.some((tag: string) => chat.marcadores.includes(tag));
          if (!hasTag) return discardMessage();
        }
        return true;
      }) ?? [];

      Promise.resolve().then(() => {
        chatDispatch({
          type: chatDispatchAction.SET_CHATS_CURSOR as ActionType,
          payload: cursor,
        });
        chatDispatch({
          type: chatDispatchAction.SET_CHATS_LIST_CURSOR as ActionType,
          payload: { items: filteredItems, prevCursor, preservarSelecao },
        });
        chatDispatch({
          type: chatDispatchAction.SET_HAS_MORE_CHATS as ActionType,
          payload: cursor != null,
        });
        chatDispatch({
          type: chatDispatchAction.SET_LOADING_CHATS as ActionType,
          payload: false,
        });
      });

      logger('chats received and loading complete');
      if (filteredItems && filteredItems.length > 0) {
        onChatsUpdated(filteredItems);
      }
      onLoadingChats(false);
    },
    [chatDispatch, onChatsUpdated, onLoadingChats, query],
  );

  const handleChat = useCallback(
    (data: any) => {
      // Filtragem do evento individual de chat
      if (query.canalId && data.canal_id !== query.canalId) return discardMessage();
      if (query.chatStatus && data.estado !== query.chatStatus) return discardMessage();
      if (!matchesResponsavelFilter(data)) return discardMessage();
      if (query.search && !(`${data.nome}`.toLowerCase().includes(query.search.toLowerCase()) || `${data.canal_cliente_id}`.includes(query.search) || `${data.ultmsg_conteudo_ou_url}`.includes(query.search))) return discardMessage();
      if (query.dataInicio && data.ultmsg_data_criacao && new Date(data.ultmsg_data_criacao) < new Date(query.dataInicio)) return discardMessage();
      if (query.dataFim && data.ultmsg_data_criacao && new Date(data.ultmsg_data_criacao) > new Date(query.dataFim)) return discardMessage();
      if (query.chatTags && query.chatTags.length > 0) {
        if (!data.marcadores || data.marcadores.length === 0) return discardMessage();
        const hasTag = query.chatTags.some((tag: string) => data.marcadores.includes(tag));
        if (!hasTag) return discardMessage();
      }

      // Gestores/admin podem ver chats finalizados dependendo do filtro,
      // mas se o chat foi finalizado e o usuário é USUARIO/VENDEDOR, deve remover
      if (data.estado === 'finalizado') {
        if (['USUARIO', 'VENDEDOR'].includes(userRole!)) {
          chatDispatch({
            type: chatDispatchAction.REMOVE_CHAT as ActionType,
            payload: { id: data.id },
          });
        } else if (['GESTOR', 'ADMIN'].includes(userRole!)) {
          chatDispatch({
            type: chatDispatchAction.UPDATE_CHAT as ActionType,
            payload: { chat: { ...data }, move: true },
          });
        }
        return;
      }

      chatDispatch({
        type: chatDispatchAction.UPSERT_CHAT as ActionType,
        payload: { chat: { ...data, __fila_accepted: data.estado == 'atendimento' && data.usuario_responsavel_id == userId }, move: true },
      });
    },
    [chatDispatch, query, userId, userRole],
  );

  const handleChatRemove = useCallback(
    (data: any) => {
      chatDispatch({
        type: chatDispatchAction.REMOVE_CHAT as ActionType,
        payload: data,
      });
    },
    [chatDispatch],
  );

  const handleChatMessageSent = useCallback(
    (/* data: any */) => {
      chatDispatch({
        type: chatDispatchAction.SET_SENDING_MESSAGE as ActionType,
        payload: false,
      });
    },
    [chatDispatch],
  );

  const handleChatMessageError = useCallback(
    (data: any) => {
      chatDispatch({
        type: chatDispatchAction.SET_SENDING_MESSAGE as ActionType,
        payload: false,
      });
      console.log('chat.message.error', data);

      // O campo de texto é limpo assim que `sendingMessage` volta a false, então
      // um erro silencioso faria a atendente acreditar que a mensagem saiu.
      if (data?.motivo === 'destinatario_divergente') {
        toast(
          'A mensagem NÃO foi enviada: a conversa aberta não confere com o destinatário. Abra a conversa novamente e reenvie.',
          {
            toastId: 'chat_message_destinatario_divergente',
            position: 'top-center',
            type: 'error',
            autoClose: false,
          },
        );
      } else {
        toast('Não foi possível enviar a mensagem. Tente novamente.', {
          toastId: 'chat_message_error',
          position: 'top-center',
          type: 'error',
          autoClose: 8000,
        });
      }
    },
    [chatDispatch],
  );

  const handleChatMessage = useCallback(
    (data: any) => {
      // Verificar se temos dados válidos antes de processar
      if (!data) {
        logger('received empty chat message data');
        return;
      }

      logger('received chat message', {
        id: data.id,
        origem: data.origem,
        chat_id: data.chat?.id,
        correlationId: data.correlationId,
        originatedBy: data.originatedBy,
      });

      const chat = data.chat ?? {};

      // Antes dos filtros da lista: o som vale para qualquer conversa do atendente.
      if (data.origem === 'usuario' && userId && chat.usuario_responsavel_id === userId) {
        tocarSomMensagem(isPageActive.current ?? true);
      }

      // Filtragem por canal, status e busca
      const canalId = chat.canal?.id || chat.canal_id;
      if (query.canalId && query.canalId !== canalId) return discardMessage();
      if (query.chatStatus && chat.estado !== query.chatStatus) return discardMessage();
      if (!matchesResponsavelFilter(chat)) return discardMessage();
      if (query.search && !(`${chat.nome}`.toLowerCase().includes(query.search.toLowerCase()) || `${chat.canal_cliente_id}`.includes(query.search) || `${chat.ultmsg_conteudo_ou_url}`.includes(query.search))) return discardMessage();
      if (query.dataInicio && data.data_criacao && new Date(data.data_criacao) < new Date(query.dataInicio)) return discardMessage();
      if (query.dataFim && data.data_criacao && new Date(data.data_criacao) > new Date(query.dataFim)) return discardMessage();
      if (query.chatTags && query.chatTags.length > 0) {
        if (!chat.marcadores || chat.marcadores.length === 0) return discardMessage();
        const hasTag = query.chatTags.some((tag: string) => chat.marcadores.includes(tag));
        if (!hasTag) return discardMessage();
      }

      // Verificar se a mensagem já existe no estado para evitar duplicação
      if (chatState.selectedChatClientId === chat.canal_cliente_id?.toString() &&
        chatState.selectedChatMessages?.some((msg: any) => msg.id === data.id)) {
        logger('message already exists, skipping', { messageId: data.id });
        return;
      }

      chatDispatch({
        type: chatDispatchAction.SET_SENDING_MESSAGE as ActionType,
        payload: false,
      });

      if (chatState.selectedChatClientId === chat.canal_cliente_id?.toString()) {
        chatDispatch({
          type: chatDispatchAction.ADD_CHAT_MESSAGE as ActionType,
          payload: {
            chat,
            id: data.id,
            canal: data.canal,
            origem: data.origem,
            conteudo_ou_url: data.conteudo_ou_url,
            tipo_mime: data.tipo_mime,
            data_criacao: data.data_criacao,
            metadados: data.metadados,
            estado_atual: data.estado_atual,
          },
        });
      }

      if (data.origem === 'sistema') {
        chatDispatch({
          type: chatDispatchAction.UPSERT_CHAT as ActionType,
          payload: {
            chat: {
              ...chat,
              __fila_accepted: chat.estado === 'atendimento' && chat.usuario_responsavel_id === userId
            },
            move: true,
          },
        });
      } else if (data.estado_atual !== 'erro') {
        chatDispatch({
          type: chatDispatchAction.UPSERT_CHAT as ActionType,
          payload: {
            chat: {
              ...chat,
              ultmsg_conteudo_ou_url: data.conteudo_ou_url,
              ultmsg_data_criacao: data.data_criacao,
              ultmsg_tipo_mime: data.tipo_mime,
              ultmsg_origem: data.origem,
              ultmsg_metadados: data.metadados,
            },
            move: data.origem !== 'atendente',
          },
        });
      }
    },
    [query, chatDispatch, userId, chatState.selectedChatClientId, chatState.selectedChatMessages],
  );

  const handleChatMessageStatus = useCallback(
    (data: any) => {
      chatDispatch({
        type: chatDispatchAction.UPDATE_CHAT_MESSAGE as ActionType,
        payload: data,
      });
    },
    [chatDispatch],
  );

  const handleChatMessageReaction = useCallback(
    (data: any) => {
      chatDispatch({
        type: chatDispatchAction.UPDATE_CHAT_MESSAGE as ActionType,
        payload: data,
      });
    },
    [chatDispatch],
  );

  // Mensagem editada na origem (echo `edit` do app WhatsApp Business):
  // o conteúdo novo vem no payload e substitui o antigo.
  const handleChatMessageEdit = useCallback(
    (data: any) => {
      chatDispatch({
        type: chatDispatchAction.UPDATE_CHAT_MESSAGE as ActionType,
        payload: data,
      });
    },
    [chatDispatch],
  );

  // Mensagem apagada na origem (echo `revoke`): sai da lista.
  const handleChatMessageRemove = useCallback(
    (data: any) => {
      chatDispatch({
        type: chatDispatchAction.REMOVE_CHAT_MESSAGE as ActionType,
        payload: data,
      });
    },
    [chatDispatch],
  );

  const handleChatMessagesReload = useCallback((data: any) => {
    if (!Array.isArray(data)) {
      logger('received invalid chat messages data for reload', data);
      chatDispatch({
        type: chatDispatchAction.LOADING_MESSAGES as ActionType,
        payload: false,
      });
      return;
    }

    // Constante para paginação - melhor ter uma constante do que números mágicos
    const PAGE_SIZE = 50;
    const hasMorePages = data.length >= PAGE_SIZE;

    logger('messages reload complete', {
      messageCount: data.length,
      hasMorePages
    });

    // Agrupar dispatches para reduzir renderizações
    Promise.resolve().then(() => {
      chatDispatch({
        type: chatDispatchAction.SET_HAS_MORE_PAGES as ActionType,
        payload: hasMorePages,
      });
      chatDispatch({
        type: chatDispatchAction.SET_CHAT_MESSAGES as ActionType,
        payload: data,
      });
      chatDispatch({
        type: chatDispatchAction.LOADING_MESSAGES as ActionType,
        payload: false,
      });
    });
  }, []);

  const handleChatMessages = useCallback((data: any) => {
    if (!Array.isArray(data)) {
      logger('received invalid chat messages data', data);
      chatDispatch({
        type: chatDispatchAction.LOADING_MESSAGES as ActionType,
        payload: false,
      });
      return;
    }

    // Constante para paginação - melhor ter uma constante do que números mágicos
    const PAGE_SIZE = 50;
    const hasMorePages = data.length >= PAGE_SIZE;

    logger('messages fetch complete', {
      messageCount: data.length,
      hasMorePages
    });

    // Agrupar dispatches para reduzir renderizações
    Promise.resolve().then(() => {
      chatDispatch({
        type: chatDispatchAction.SET_HAS_MORE_PAGES as ActionType,
        payload: hasMorePages,
      });
      chatDispatch({
        type: chatDispatchAction.ADD_CHAT_MESSAGES as ActionType,
        payload: data,
      });
      chatDispatch({
        type: chatDispatchAction.LOADING_MESSAGES as ActionType,
        payload: false,
      });
    });
  }, []);

  const handleFila = useCallback(
    (data: any) => {
      chatDispatch({
        type: chatDispatchAction.SET_FILA as ActionType,
        payload: data.list,
      });
    },
    [chatDispatch],
  );

  const handleFilaChamada = useCallback(
    (data: any) => {
      console.log('!!! fila.chamada !!!', data);
      chatDispatch({
        type: chatDispatchAction.ADD_FILA as ActionType,
        payload: data,
      });
    },
    [chatDispatch],
  );

  const handleFilaChamadaFim = useCallback(
    (data: any) => {
      const { chat_id, canal_id, canal_cliente_id, usuario_responsavel_id } = data;

      if (
        userId &&
        data.reason === 'accepted' &&
        usuario_responsavel_id === userId
      ) {
        // fui eu! Confirmar recebimento para o backend não considerar o aceite
        // órfão (o watchdog devolve à fila aceites nunca confirmados).
        socket.emit('fila.atendimento.confirmar', { chat_id });

        // fui eu! Colorir o chat
        chatDispatch({
          type: chatDispatchAction.UPSERT_CHAT as ActionType,
          payload: {
            chat: {
              id: chat_id,
              canal_id,
              canal_cliente_id,
              __fila_accepted: true,
            },
            move: true,
          },
        });

        if (!chatState.selectedChatClientId) {
          chatDispatch({
            type: chatDispatchAction.SET_SELECTED_CHAT_ID as ActionType,
            payload: {
              // O reducer lê `chat_id`; enviar `id` deixava `selectedChatId`
              // indefinido nesta rota, e é ele que serve de conferência de
              // destinatário no envio.
              chat_id,
              canal_id,
              canal_cliente_id,
            },
          });
          socket.emit('chat.join', {
            chat_id,
            canal_id,
            canal_cliente_id,
          });
        }
      } else {
        if (data.reason === 'accepted') {
          // não fui eu... notificar...
          toast('A solicitação foi aceita por outro atendente', {
            toastId: 'fila_chamada_fim_already_accepted',
            position: 'top-center',
            type: 'info',
            autoClose: 5000,
          });
        }
      }

      console.log('[=(] fila.chamada.fim [=(]', data);
      chatDispatch({
        type: chatDispatchAction.REMOVE_FILA as ActionType,
        payload: data,
      });
    },
    [chatDispatch],
  );

  const handleFilaCall = useCallback((data: any) => {
    if (!data) return;

    chatDispatch({
      type: chatDispatchAction.SET_FILA_CALL as ActionType,
      payload: data,
    });

    logger('received fila call', data);
  }, []);

  const handleFilaCallAdd = useCallback((data: any) => {
    if (!data) return;

    chatDispatch({
      type: chatDispatchAction.ADD_FILA_CALL as ActionType,
      payload: data,
    });

    logger('received fila call add', data);
  }, []);


  const handleFilaCallOffer = useCallback((data: any) => {
    if (!data) return;

    const { chamada } = data;

    chatDispatch({
      type: chatDispatchAction.ADD_FILA_CALL as ActionType,
      payload: chamada,
    });

    logger('received fila call offer', data);
  }, []);

  const handleFilaCallEnd = useCallback(
    (data: any) => {
      const { reason, id, usuario_responsavel_id } = data;

      if (
        userId &&
        reason === 'accepted' &&
        usuario_responsavel_id === userId
      ) {
        // fui eu!

        onAcceptedCall(id, usuario_responsavel_id);
      } else {
        if (reason === 'accepted') {
          // não fui eu... notificar...
          toast('A chamada foi aceita por outro atendente', {
            toastId: 'fila_call_end_already_accepted',
            position: 'top-center',
            type: 'info',
            autoClose: 5000,
          });
        } else if (reason === 'client_hangup') {
          // cliente desligou
          toast('O cliente encerrou a chamada', {
            toastId: 'fila_call_end_client_hangup',
            position: 'top-center',
            type: 'info',
            autoClose: 5000,
          });
        }
      }

      console.log('[=(] fila.call.end [=(]', data);
      chatDispatch({
        type: chatDispatchAction.REMOVE_FILA_CALL as ActionType,
        payload: data,
      });
    },
    [chatDispatch],
  );

  // Handler para aviso de dispositivos de áudio
  const handleAudioDevicesWarning = useCallback(
    (data: { message: string; canHandleCalls: boolean }) => {
      logger('received audio.devices.warning', data);

      chatDispatch({
        type: chatDispatchAction.SET_AUDIO_DEVICES_WARNING as ActionType,
        payload: data.message,
      });

      chatDispatch({
        type: chatDispatchAction.SET_CAN_HANDLE_CALLS as ActionType,
        payload: data.canHandleCalls,
      });

      // Mostrar toast de aviso apenas se houver canais VoIP
      if (hasVoipCanais) {
        toast(data.message, {
          toastId: 'audio_devices_warning',
          position: 'top-center',
          type: 'warning',
          autoClose: false, // Não fechar automaticamente
        });
      }
    },
    [chatDispatch, hasVoipCanais],
  );

  // Função para enviar status de dispositivos de áudio ao servidor
  const sendAudioDevicesStatus = useCallback(
    (status: { hasMicrophone: boolean; hasSpeakers: boolean; hasPermission: boolean }) => {
      if (!socket.connected) {
        logger('socket not connected, cannot send audio devices status');
        return;
      }

      logger('sending audio devices status', status);
      socket.emit('audio.devices', status);

      // Atualizar estado local
      const canHandle = status.hasMicrophone && status.hasPermission;
      chatDispatch({
        type: chatDispatchAction.SET_CAN_HANDLE_CALLS as ActionType,
        payload: canHandle,
      });

      // Só atualizar aviso se houver canais VoIP cadastrados
      if (!canHandle && hasVoipCanais) {
        const message = !status.hasMicrophone
          ? 'Nenhum microfone detectado. Você não receberá chamadas de voz.'
          : 'Permissão de microfone necessária para receber chamadas de voz.';

        chatDispatch({
          type: chatDispatchAction.SET_AUDIO_DEVICES_WARNING as ActionType,
          payload: message,
        });
      } else {
        chatDispatch({
          type: chatDispatchAction.SET_AUDIO_DEVICES_WARNING as ActionType,
          payload: null,
        });
      }
    },
    [socket, chatDispatch, hasVoipCanais],
  );

  const handlersRef = useRef<Record<string, (...args: any[]) => void>>({});
  useEffect(() => {
    handlersRef.current = {
      // Eventos de conexão
      'connect': handleConnect,
      'disconnect': handleDisconnect,
      'reconnection_attempt': handleReconnectionAttempt,
      'reconnect': handleReconnect,
      'connect_error': handleConnectError,

      // Eventos de chats
      'chats': handleChats,
      'chat': handleChat,
      'chat.remove': handleChatRemove,

      // Eventos de mensagens
      'chat.message': handleChatMessage,
      'chat.message.sent': handleChatMessageSent,
      'chat.message.error': handleChatMessageError,
      'chat.message.status': handleChatMessageStatus,
      'chat.message.reaction': handleChatMessageReaction,
      'chat.message.edit': handleChatMessageEdit,
      'chat.message.remove': handleChatMessageRemove,
      'chat.messages.reload': handleChatMessagesReload,
      'chat.messages': handleChatMessages,

      // Eventos de fila
      'fila': handleFila,
      'fila.chamada': handleFilaChamada,
      'fila.chamada.fim': handleFilaChamadaFim,

      // Eventos de chamada
      'fila.call': handleFilaCall,
      'fila.call.add': handleFilaCallAdd,
      'fila.call.offer': handleFilaCallOffer,
      'fila.call.end': handleFilaCallEnd,

      // Eventos de dispositivos de áudio
      'audio.devices.warning': handleAudioDevicesWarning,

      // Eventos diversos
      'params': handleParams,
    };
  }, [
    handleConnect,
    handleDisconnect,
    handleReconnectionAttempt,
    handleReconnect,
    handleConnectError,
    handleChats,
    handleChat,
    handleChatRemove,
    handleChatMessage,
    handleChatMessageSent,
    handleChatMessageError,
    handleChatMessageStatus,
    handleChatMessageReaction,
    handleChatMessageEdit,
    handleChatMessageRemove,
    handleChatMessagesReload,
    handleChatMessages,
    handleFila,
    handleFilaChamada,
    handleFilaChamadaFim,
    handleFilaCall,
    handleFilaCallAdd,
    handleFilaCallOffer,
    handleFilaCallEnd,
    handleAudioDevicesWarning,
    handleParams,
  ]);

  // Efeito para registrar os handlers de eventos
  useEffect(() => {
    if (!socket) return

    const _socket = socket;

    logger('registering event handlers');

    // Primeiro remover todos os event listeners existentes para evitar duplicação
    if (_socket) {
      _socket.off('chats')
      _socket.off('chat');
      _socket.off('chat.remove');
      _socket.off('chat.message');
      _socket.off('chat.messages');
      _socket.off('chat.messages.reload');
      _socket.off('chat.message.sent');
      _socket.off('chat.message.error');
      _socket.off('chat.message.status');
      _socket.off('chat.message.reaction');
      _socket.off('chat.message.edit');
      _socket.off('chat.message.remove');

      _socket.off('fila');
      _socket.off('fila.chamada');
      _socket.off('fila.chamada.fim');

      _socket.off('fila.call');
      _socket.off('fila.call.offer');
      _socket.off('fila.call.end');
    }

    const createDelegatingHandler = (event: string) => (...args: any[]) => {
      handlersRef.current[event]?.(...args);
    };

    const delegatedHandlers: Record<string, (...args: any[]) => void> = {};

    const events = Object.keys(handlersRef.current);

    events.forEach(event => {
      delegatedHandlers[event] = createDelegatingHandler(event);
      socket.on(event, delegatedHandlers[event]);
    });

    socket.connect();

    // Se o socket já estiver conectado (caso de hot reload), atualizar o estado imediatamente
    if (socket.connected) {
      setWsState('connected');
    }

    return () => {
      logger('removing event handlers');

      if (_socket) {
        events.forEach(event => {
          _socket.off(event, delegatedHandlers[event]);
        });
        _socket.disconnect();
      }
    };

  }, [socket]);

  const chatAddTag = useCallback(
    (chatId: number, tagId: number) => {
      socket.emit('chat.tag.add', { chat_id: chatId, tag_id: tagId });
    },
    [socket],
  );

  const reloadChat = useCallback(
    (chatId: number) => {
      if (!socket.connected) return;

      socket.emit('chat.reload', {
        chat_id: chatId,
        canal_cliente_id: chatState.selectedChatClientId,
      });
    },
    [socket, chatState.selectedChatClientId],
  );

  const reloadChats = useCallback(
    (options: any = null, force = false, preservarSelecao = false) => {
      if (!socket.connected) {
        logger('socket not connected, cannot reload chats');
        return;
      }

      if (!chatState.loadingChats || force === true) {
        logger('reloading chats', { force });

        // Notificar componentes pai sobre o estado de carregamento
        onLoadingChats(true);

        chatDispatch({
          type: chatDispatchAction.SET_LOADING_CHATS as ActionType,
          payload: true,
        });

        preservarSelecaoRef.current = preservarSelecao;

        socket.emit('chats.reload', options, (ack: any) => {
          if (ack?.status === 'success') {
            logger('chats reload acknowledged', ack);
            return;
          }

          // Erro ou throttle do servidor: não virá evento `chats`. Sem desfazer
          // o estado de carregamento aqui o botão "atualizar" (desabilitado
          // enquanto carrega) ficaria travado, e a marca de preservação
          // vazaria para o próximo reload.
          logger('chats reload not performed', ack);
          preservarSelecaoRef.current = false;
          chatDispatch({
            type: chatDispatchAction.SET_LOADING_CHATS as ActionType,
            payload: false,
          });
          onLoadingChats(false);
        });
      } else {
        logger('skipping chat reload - already loading or force not set');
      }
    },
    [chatState.loadingChats, socket, onLoadingChats],
  );

  // Controle de debounce para loadMoreChats
  const loadMoreChatsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastRequestedPage = useRef<number>(-1);

  // Memoize loadMoreChats com debounce integrado para evitar múltiplas chamadas
  const loadMoreChats = useCallback((page: number) => {
    logger('loadMoreChats requested', { page });

    // Evitar carregamento duplicado da mesma página
    if (page === lastRequestedPage.current) {
      logger('skipping loadMoreChats - same page requested again', { page });
      return;
    }

    // Verificar se já estamos carregando ou não há mais chats
    if (chatState.loadingChats) {
      logger('skipping loadMoreChats - already loading');
      return;
    }

    if (!chatState.hasMoreChats) {
      logger('skipping loadMoreChats - no more chats available');
      return;
    }

    // Cancelar timer anterior de debounce se existir
    if (loadMoreChatsTimeoutRef.current) {
      clearTimeout(loadMoreChatsTimeoutRef.current);
    }

    // Atualizar a referência da última página solicitada
    lastRequestedPage.current = page;

    // Aplicar debounce de 100ms para evitar múltiplas chamadas
    loadMoreChatsTimeoutRef.current = setTimeout(() => {
      logger('executing loadMoreChats after debounce', { page });
      reloadChats(
        {
          pageParams: chatState.chatsCursor,
          search: query.search,
          chatStatus: query.chatStatus,
          canalId: query.canalId,
          userId: query.userId,
          mode: query.mode,
          apenasMeus: query.apenasMeus,
          usuarioResponsavelId: query.usuarioResponsavelId,
          grupoResponsavelId: query.grupoResponsavelId,
        },
        true,
      );

      loadMoreChatsTimeoutRef.current = null;
    }, 300);

  }, [chatState.loadingChats, chatState.hasMoreChats, chatState.chatsCursor, query, reloadChats]);

  const selectChat = useCallback(
    (chat: ChatDto) => {
      chatDispatch({
        type: chatDispatchAction.SET_CHAT_MESSAGES as ActionType,
        payload: null,
      });
      if (!chat || chatState.selectedChatId == chat?.id) {
        return chatDispatch({
          type: chatDispatchAction.SET_SELECTED_CHAT_ID as ActionType,
          payload: null,
        });
      }

      // Itens vindos de `listFe` trazem `canal_id` plano; os montados a partir
      // de eventos de socket (fila, mensagem) podem só ter `canal.id`. Sem esse
      // fallback a seleção fica sem canal e o UPSERT abaixo é descartado pelo
      // reducer, que exige `canal_id`.
      const canalId = (chat as any).canal_id ?? chat.canal?.id ?? null;

      chatDispatch({
        type: chatDispatchAction.SET_SELECTED_CHAT_ID as ActionType,
        payload: {
          chat_id: chat.id,
          canal_id: canalId,
          canal_cliente_id: chat.canal_cliente_id,
        },
      });
      chatDispatch({
        type: chatDispatchAction.UPSERT_CHAT as ActionType,
        payload: {
          chat: {
            id: chat.id,
            canal_id: canalId,
            canal_cliente_id: chat.canal_cliente_id,
            __fila_accepted: false,
          },
          move: false,
        },
      });
      chatDispatch({
        type: chatDispatchAction.LOADING_MESSAGES as ActionType,
        payload: true,
      });
      socket.emit('chat.join', {
        chat_id: chat?.id ?? null,
        canal_id: canalId,
        canal_cliente_id: chat?.canal_cliente_id ?? null,
      });
    },
    [chatState.selectedChatId, socket],
  );

  const sendMessage = useCallback(
    (message: ChatMessagePayload) => {
      chatDispatch({
        type: chatDispatchAction.SET_SENDING_MESSAGE as ActionType,
        payload: true,
      });
      socket.emit('chat.message.send', {
        ...message,
        correlationId: nanoid(),
        originatedBy: 'ws/message-send',
      });
    },
    [socket, chatDispatch],
  );

  const atenderFila = useCallback(
    (chatId: number) => {
      socket.emit('fila.atender', { chat_id: chatId });
    },
    [socket],
  );

  const atenderChamada = useCallback(
    (chamadaId: number) => {
      socket.emit('fila.call.answer', {
        chamada_id: chamadaId,
      });

      chatDispatch({
        type: chatDispatchAction.UPDATE_FILA_CALL as ActionType,
        payload: {
          id: chamadaId,
          update: {
            estado: "aguarde"
          },
        },
      });
    },
    [socket],
  );

  const sendNotification = (
    chatId: number | null,
    canal_cliente_id: string,
    mensagemProgramadaId: number,
    mensagemProgramadaParams: Record<string, string> = {},
  ) => {
    const msg = {
      chat_id: chatId,
      canal_cliente_id: canal_cliente_id,
      mensagem_programada_id: mensagemProgramadaId,
      mensagem_programada_params: mensagemProgramadaParams,
    };

    chatDispatch({
      type: chatDispatchAction.SET_SENDING_MESSAGE as ActionType,
      payload: true,
    });
    wsInstance.current?.emit('chat.message.notification', msg);
  };

  const sendAudio = (
    canal_cliente_id: string,
    canal_id: number,
    audio: Blob,
    chat_id?: number | null,
  ) => {
    const msg: ChatMessagePayload = {
      canal_cliente_id,
      canal_id,
      chat_id: chat_id ?? null,
      attachments: [
        {
          name: `audio-${nanoid()}.ogg`,
          type: 'audio/ogg',
          size: audio.size,
          buffer: audio,
        },
      ],
      __trace: 18,
      correlationId: nanoid(),
      originatedBy: 'ws/message-send',
    };

    chatDispatch({
      type: chatDispatchAction.SET_SENDING_MESSAGE as ActionType,
      payload: true,
    });
    wsInstance.current?.emit('chat.message.send', msg);
  };

  // Função para enviar o status de digitação com debounce para evitar muitas emissões
  const sendTypingStatusImpl = useCallback(
    (isTyping: boolean) => {
      if (!chatState.selectedChatClientId) return;

      wsInstance.current?.emit('chat.typing', {
        chat_id: chatState.selectedChatId,
        canal_cliente_id: chatState.selectedChatClientId,
        is_typing: isTyping,
      });
    },
    [chatState.selectedChatId, chatState.selectedChatClientId]
  );

  // Aplicamos debounce fora da fase de renderização, sem usar hooks
  // Criamos uma referência para armazenar a função com debounce
  const debouncedSendTypingRef = useRef<(isTyping: boolean) => void>();

  // Atualizamos a referência quando sendTypingStatusImpl mudar
  useEffect(() => {
    debouncedSendTypingRef.current = createDebounce(sendTypingStatusImpl, 300);
  }, [sendTypingStatusImpl]);

  // Função wrapper que chama a implementação com debounce
  const sendTypingStatus = useCallback((isTyping: boolean) => {
    debouncedSendTypingRef.current?.(isTyping);
  }, []);

  const loadMore = (dt: string) => {
    chatDispatch({
      type: chatDispatchAction.LOADING_MESSAGES as ActionType,
      payload: true,
    });

    wsInstance.current?.emit('chat.fetch', {
      chat_id: chatState.selectedChatId ?? null,
      canal_id: chatState.selectedChatCanalId ?? null,
      canal_cliente_id: chatState.selectedChatClientId ?? null,
      data_criacao: dt,
    });
  };

  // Efeito para limpar timers quando o componente for desmontado
  useEffect(() => {
    logger('initializing hook');

    return () => {
      if (loadMoreChatsTimeoutRef.current) {
        clearTimeout(loadMoreChatsTimeoutRef.current);
        loadMoreChatsTimeoutRef.current = null;
      }

      if (debouncedSendTypingRef.current) {
        // Como debouncedSendTypingRef contém uma função com closure que mantém um timeout,
        // precisamos garantir que não temos referências para ela
        debouncedSendTypingRef.current = undefined;
      }
    };
  }, []);

  return {
    socket,
    wsState,
    chatState,
    reloadChat,
    chatAddTag,
    reloadChats,
    selectChat,
    loadMoreChats,
    sendMessage,
    sendNotification,
    atenderFila,
    atenderChamada,
    sendAudio,
    loadMore,
    sendTypingStatus,
    sendAudioDevicesStatus,
  };
};

export default useWebsocket;
